import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  Center,
} from "@chakra-ui/react";

// Mapping key to display properties
const metricDetails = {
  pm25: { unit: "µg/m³", name: "PM2.5" },
  pm10: { unit: "µg/m³", name: "PM10" },
  tsp: { unit: "µg/m³", name: "TSP" },
  noise: { unit: "dB", name: "Noise" },
  temperature: { unit: "°C", name: "Temperature" },
  humidity: { unit: "%", name: "Humidity" },
};

// --- REQUIREMENT 1: นำค่าเกณฑ์และ Logic การเปลี่ยนสีเข้ามาใช้ ---
const sensorThresholds = {
  pm25: { warning: 37.5, danger: 80 },
  pm10: { warning: 80, danger: 120 },
  tsp: { warning: 200, danger: 330 },
  noise: { warning: 70, danger: 115 },
};

const getValueColor = (key, val) => {
  const limits = sensorThresholds[key];
  if (!limits || val === null || val === undefined) {
    return "blue.500"; // สีปกติ
  }
  if (val > limits.danger) {
    return "red.500"; // เกินเกณฑ์อันตราย
  }
  if (val > limits.warning) {
    return "orange.400"; // เกินเกณฑ์เฝ้าระวัง
  }
  return "blue.500"; // ปกติ
};

export default function MetricDisplay({
  metric,
  realtimeData,
  hourlyData,
  chartType,
  showAvg,
  showMin,
  showMax,
  columnCount,
}) {
  const cardBg = useColorModeValue("white", "gray.700");
  const details = metricDetails[metric.key] || { unit: "", name: metric.label };
  const currentValue = realtimeData?.[metric.key];
  const valueColor = getValueColor(metric.key, currentValue);

  const responsiveFontSize = useMemo(() => {
    // ถ้าแสดงแค่ 1 คอลัมน์ ให้ตัวใหญ่พิเศษ
    if (columnCount === 1) {
      return { base: "4.5rem", md: "5.5rem", lg: "6.5rem" };
    }
    // ถ้าแสดงหลายคอลัมน์ ขนาดจะเล็กลง
    return { base: "3rem", md: "4rem", lg: "6rem" };
  }, [columnCount]);

  const { chartOptions, chartSeries } = useMemo(() => {
    const categories = hourlyData.map((d) =>
      new Date(d.hourStart).toLocaleTimeString("th-TH", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );

    let series = [];
    const metricKeyUpper =
      metric.key.charAt(0).toUpperCase() + metric.key.slice(1);

    // --- REQ 2: ปรับ Logic การสร้าง Series ใหม่ทั้งหมด ---
    if (chartType === "bar") {
      // --- Logic สำหรับ Stacked Bar Chart แบบหักลบค่า ---
      const minData = showMin
        ? hourlyData.map((d) => d.data[`min${metricKeyUpper}`] || 0)
        : [];
      const avgData = showAvg
        ? hourlyData.map((d) => d.data[`avg${metricKeyUpper}`] || 0)
        : [];
      const maxData = showMax
        ? hourlyData.map((d) => d.data[`max${metricKeyUpper}`] || 0)
        : [];

      // Base series (Min)
      if (showMin) series.push({ name: "Min", data: minData });

      // Middle series (Avg - Min)
      if (showAvg && showMin) {
        series.push({
          name: "Avg-Min",
          data: avgData.map((avg, i) => (avg - minData[i]).toFixed(1)),
        });
      } else if (showAvg) {
        // กรณีมีแค่ Avg ไม่มี Min
        series.push({
          name: "Average",
          data: avgData.map((d) => d.toFixed(1)),
        });
      }

      // Top series (Max - Avg)
      if (showMax && showAvg) {
        series.push({
          name: "Max-Avg",
          data: maxData.map((max, i) => (max - avgData[i]).toFixed(1)),
        });
      } else if (showMax) {
        // กรณีมีแค่ Max ไม่มี Avg
        series.push({ name: "Max", data: maxData.map((d) => d.toFixed(1)) });
      }
    } else {
      // --- Logic เดิมสำหรับ Line และ Area Chart ---
      if (showMax)
        series.push({
          name: "Max",
          data: hourlyData.map(
            (d) => d.data[`max${metricKeyUpper}`]?.toFixed(1) || null
          ),
        });
      if (showAvg)
        series.push({
          name: "Average",
          data: hourlyData.map(
            (d) => d.data[`avg${metricKeyUpper}`]?.toFixed(1) || null
          ),
        });
      if (showMin)
        series.push({
          name: "Min",
          data: hourlyData.map(
            (d) => d.data[`min${metricKeyUpper}`]?.toFixed(1) || null
          ),
        });
    }

    const options = {
      colors: ['#FFC300', '#3498DB', '#2ECC71'],
      chart: {
        type: chartType,
        height: 250,
        stacked: chartType === "bar",
        animations: { enabled: true },
      },
      plotOptions: { bar: { horizontal: false, columnWidth: "60%" } },
      fill: { opacity: chartType === "area" ? [0.25, 0.45, 1] : 1 },
      stroke: { curve: "smooth", width: chartType === "line" ? 3 : 0 },
      xaxis: { categories, title: { text: "Time" } },
      yaxis: { title: { text: details.unit }, forceNiceScale: true },
      title: { text: `Hourly ${details.name}`, align: "left" },
      dataLabels: { enabled: false },
      tooltip: {
        // ทำให้ Tooltip แสดงค่าที่ถูกต้องสำหรับ Stacked Bar
        y: {
          formatter: (value, { series, seriesIndex, dataPointIndex, w }) => {
            if (chartType !== "bar") return `${value} ${details.unit}`;

            let total = 0;
            for (let i = 0; i <= seriesIndex; i++) {
              total += parseFloat(w.globals.series[i][dataPointIndex]);
            }
            return `${total.toFixed(1)} ${details.unit}`;
          },
        },
      },
    };

    return { chartOptions: options, chartSeries: series };
  }, [
    hourlyData,
    chartType,
    metric.key,
    details.name,
    details.unit,
    showAvg,
    showMin,
    showMax,
  ]);

  return (
    <Box p={5} bg={cardBg} borderRadius="lg" shadow="md">
      <VStack align="stretch" spacing={4}>
        {/* ... ส่วนแสดงผล Real-time ไม่มีการแก้ไข ... */}
        <VStack spacing={0}>
                <Heading size="md" color="gray.500" mb={2}>{details.name} (Real-time)</Heading>
                <HStack align="baseline">
                    {/* --- REQ 2: ใช้ fontSize prop แทน size prop --- */}
                    <Heading
                      fontSize={responsiveFontSize}
                      color={valueColor}
                      fontWeight="extrabold"
                      lineHeight="1"
                    >
                      {currentValue !== undefined ? currentValue.toFixed(1) : '-'}
                    </Heading>
                    <Text 
                      fontSize={columnCount === 1 ? '2xl' : 'lg'} // ปรับขนาดหน่วยตามไปด้วย
                      color="gray.500" 
                      pb={2}
                    >
                      {details.unit}
                    </Text>
                </HStack>
            </VStack>
        <Box>
          {chartSeries.length > 0 ? (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type={chartType}
              height={280}
            />
          ) : (
            <Center h="280px">
              <Text>
                Please select at least one series (Avg/Min/Max) to display.
              </Text>
            </Center>
          )}
        </Box>
      </VStack>
    </Box>
  );
}
